import { apiRequest } from '../api/api'
import { API_ENDPOINTS } from '../utils/constants'

// Admin panel data comes from the existing admin-gated endpoints. The server
// enforces requireAdmin (DB-authoritative) on every call below.
//   /api/reports/* — read-only operational reports.
//   /api/admin/*   — product catalogue read + the one write action Phase 12
//                    adds: activate / deactivate (audited server-side).
const R = API_ENDPOINTS.REPORTS
const A = API_ENDPOINTS.ADMIN

const adminService = {
  // Reports (read-only)
  getDashboard: () => apiRequest(`${R}/dashboard`),
  getPreparationToday: () => apiRequest(`${R}/preparation-today`),
  getSubscriptionWorkload: () => apiRequest(`${R}/subscription-workload`),
  getCustomers: () => apiRequest(`${R}/customers`),
  getProductQuality: () => apiRequest(`${R}/product-quality`),
  getRevenue: (from, to) =>
    apiRequest(`${R}/revenue?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  getSubscriptions: () => apiRequest(`${R}/subscriptions`),
  getTodayDeliveries: () => apiRequest(`${R}/today-deliveries`),
  getMissedMeals: () => apiRequest(`${R}/missed-meals`),

  // Product management
  getProducts: () => apiRequest(`${A}/products`),
  setProductStatus: (id, status) =>
    apiRequest(`${A}/products/${id}/status`, { method: 'PATCH', body: { status } }),

  // Merchant operations — orders + subscriptions (Phase 13 read; Phase 14 action)
  getOrders: (params = {}) => apiRequest(`${A}/orders${qs(params)}`),
  getOrder: id => apiRequest(`${A}/orders/${id}`),
  getSubscriptionsList: (params = {}) => apiRequest(`${A}/subscriptions${qs(params)}`),
  getSubscription: id => apiRequest(`${A}/subscriptions/${id}`),
  // The only order mutation: cancel an unpaid (PENDING) order.
  setOrderStatus: (id, status) =>
    apiRequest(`${A}/orders/${id}/status`, { method: 'PATCH', body: { status } }),

  // Phase 16.8A — confirm a ONE-TIME order's cash payment (pending → confirmed).
  confirmOrderCash: id => apiRequest(`${A}/orders/${id}/confirm-cash`, { method: 'PATCH', body: {} }),

  // Phase 16 — cash confirmation activates a pending subscription.
  confirmCash: id => apiRequest(`${A}/subscriptions/${id}/confirm-cash`, { method: 'PATCH', body: {} }),

  // Phase 16 — product subscription classification.
  classifyProduct: (id, body) => apiRequest(`${A}/products/${id}/classification`, { method: 'PATCH', body }),
  classifyProductsBulk: (productIds, foodType, workoutPeriod) =>
    apiRequest(`${A}/products/classification/bulk`, { method: 'PATCH', body: { product_ids: productIds, food_type: foodType, workout_period: workoutPeriod } }),

  // Phase 16 — gyms.
  getGyms: (params = {}) => apiRequest(`${A}/gyms${qs(params)}`),
  createGym: body => apiRequest(`${A}/gyms`, { method: 'POST', body }),
  updateGym: (id, body) => apiRequest(`${A}/gyms/${id}`, { method: 'PATCH', body }),
  setGymStatus: (id, status) => apiRequest(`${A}/gyms/${id}/status`, { method: 'PATCH', body: { status } }),

  // Phase 16 — gym requests.
  getGymRequests: (params = {}) => apiRequest(`${A}/gym-requests${qs(params)}`),
  setGymRequestStatus: (id, status) => apiRequest(`${A}/gym-requests/${id}/status`, { method: 'PATCH', body: { status } }),

  // Phase 16 — subscription settings.
  getSettings: () => apiRequest(`${A}/settings`),
  updateSettings: body => apiRequest(`${A}/settings`, { method: 'PATCH', body }),

  // Phase 16.4 — independent admin authentication (admin_token cookie).
  adminLogin: (email, password) => apiRequest(`${A}/auth/login`, { method: 'POST', body: { email, password } }),
  adminLogout: () => apiRequest(`${A}/auth/logout`, { method: 'POST', body: {} }),
  adminMe: () => apiRequest(`${A}/auth/me`),

  // Phase 16.4 — staff management.
  getStaff: (params = {}) => apiRequest(`${A}/staff${qs(params)}`),
  createStaff: body => apiRequest(`${A}/staff`, { method: 'POST', body }),
  setStaffStatus: (id, status) => apiRequest(`${A}/staff/${id}/status`, { method: 'PATCH', body: { status } }),
  setStaffRole: (id, role) => apiRequest(`${A}/staff/${id}/role`, { method: 'PATCH', body: { role } }),
  assignStaffGym: (id, gymId) => apiRequest(`${A}/staff/${id}/gyms`, { method: 'POST', body: { gym_id: gymId } }),
  unassignStaffGym: (id, gymId) => apiRequest(`${A}/staff/${id}/gyms/${gymId}`, { method: 'DELETE' }),

  // Phase 16.4 — gym pickup / handover.
  getPickups: (params = {}) => apiRequest(`${A}/pickups${qs(params)}`),
  markPickupReceived: id => apiRequest(`${A}/pickups/${id}/received`, { method: 'PATCH', body: {} })
}

/** Build a `?a=b&c=d` string from defined, non-empty params (encoded). */
function qs (params) {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
  return parts.length ? `?${parts.join('&')}` : ''
}

export default adminService
